import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, Loader2, Plus, ArrowUp, ArrowDown, Trash2, Pencil, ListOrdered, Play, BookmarkPlus, AlertTriangle, CheckCircle2, Users, GripVertical } from "lucide-react";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import { API_BASE } from "./constants";
import { SearchableSelect } from "./SearchableSelect";
import ModalPortal from "@/components/ui/modal-portal";

const fmt = (n, d = 2) => (n == null || Number.isNaN(Number(n)) ? "—"
  : Number(n).toLocaleString("fr-FR", { maximumFractionDigits: d }));
const frDate = (iso) => (iso && iso.length >= 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");
const addDays = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const norm = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").trim().toLowerCase();
const toNum = (v) => { const n = parseFloat(String(v ?? "").replace(",", ".")); return Number.isNaN(n) ? null : n; };

const KINDS = {
  phyto: { label: "Phyto", bg: "#2D6A4F15", fg: "#2D6A4F", units: ["L/ha", "kg/ha", "g/ha", "mL/ha", "L/hL", "kg/hL"] },
  fertilisation: { label: "Fertilisation", bg: "#8B5E3C15", fg: "#8B5E3C", units: ["kg/ha", "T/ha", "L/ha", "g/ha", "unite/ha"] },
  biostimulant: { label: "Biostimulant", bg: "#0891B215", fg: "#0E7490", units: ["L/ha", "kg/ha", "g/ha", "mL/L", "g/L", "T/ha", "unite/ha"] },
};
const kindOf = (s) => s.kind || "phyto";

/** Date de chaque étape : départ + délais cumulés (0 pour un produit du même passage). */
function stepDates(steps, start) {
  let cur = start;
  return steps.map((s, i) => {
    if (i > 0 && !s.same_pass) cur = addDays(cur, Number(s.delay_days) || 0);
    return cur;
  });
}

function KindPill({ kind }) {
  const k = KINDS[kind] || KINDS.phyto;
  return <span className="text-[10px] font-semibold rounded-full px-1.5 py-0.5 shrink-0" style={{ background: k.bg, color: k.fg }}>{k.label}</span>;
}

function Chain({ steps }) {
  return (
    <div className="flex flex-wrap items-center gap-1 text-[11px] text-gray-500">
      {steps.map((s, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          {i > 0 && <span className="text-[10px] text-gray-400">{s.same_pass ? "+ même passage" : `→ J+${s.delay_days || 0}`}</span>}
          <span className="rounded-md px-1.5 py-0.5 text-gray-800" style={{ background: KINDS[kindOf(s)].bg }}>{s.product?.commercial_name}</span>
        </span>
      ))}
    </div>
  );
}

/** Catalogues pour l'éditeur : phyto (E-Phy), fertilisation et biostimulants du référentiel. */
function useCatalogs(refData) {
  return useMemo(() => {
    const phyto = Object.entries(refData?.produits_phyto || {}).flatMap(([cat, list]) => (Array.isArray(list) ? list : []).map((p) => {
      const amm = String(p.amm || "").match(/(\d{7})/);
      return {
        value: `phyto:${amm ? amm[1] : p.name}`,
        label: `${p.name}${amm ? ` (AMM ${amm[1]})` : ""}`,
        sublabel: p.matiere_active || "",
        step: {
          kind: "phyto",
          product: { amm_number: amm ? amm[1] : p.amm || "", commercial_name: p.name, active_substances: p.matiere_active ? [p.matiere_active] : [], category: cat, product_type: "PPP" },
          dose_default: p.dose_default, dar: p.dar,
        },
      };
    }));
    const ferti = (refData?.amendements || []).map((p) => ({
      value: `fertilisation:${p.name}`, label: p.name, sublabel: p.category === "amendement" ? "Amendement" : "Engrais",
      step: { kind: "fertilisation", product: { commercial_name: p.name, category: p.category }, dose_default: p.dose_default, unit: p.unit },
    }));
    const bio = (refData?.biostimulants || []).map((p) => ({
      value: `biostimulant:${p.name}`, label: p.name, sublabel: p.product_type || "Biostimulant",
      step: { kind: "biostimulant", product: { commercial_name: p.name, category: p.product_type || "biostimulant" }, dose_default: p.dose_default, unit: p.unit },
    }));
    return { phyto, fertilisation: ferti, biostimulant: bio };
  }, [refData]);
}

export default function ItinerariesModal({ parcelId, parcelName, ilotId, ilots = [], parcelSurface, parcelCulture, refData, onClose, onApplied }) {
  const [view, setView] = useState("list"); // list | apply | edit | fromCarnet | done
  const [data, setData] = useState(null);
  const [current, setCurrent] = useState(null);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [targets, setTargets] = useState({});
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [addKind, setAddKind] = useState("phyto");
  const [source, setSource] = useState(null);
  const [picked, setPicked] = useState({});
  const [fromName, setFromName] = useState("");
  const [result, setResult] = useState(null);
  const catalogs = useCatalogs(refData);

  const load = useCallback(() => {
    authFetch(`${API_BASE}/itineraries`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => toast.error("Chargement des itinéraires impossible"));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Cibles possibles : la parcelle entière et chacun de ses îlots
  const targetRows = useMemo(() => [
    { key: "", ilot_id: null, label: `${parcelName || "Parcelle"} (parcelle entière)`, surface: parcelSurface, culture: parcelCulture },
    ...ilots.map((i) => ({ key: i.ilot_id, ilot_id: i.ilot_id, label: `Îlot ${i.name}`, surface: i.surface_ha || parcelSurface, culture: i.culture || parcelCulture })),
  ], [ilots, parcelName, parcelSurface, parcelCulture]);
  const chosenTargets = targetRows.filter((t) => targets[t.key]);

  // ---------- Appliquer ----------
  const openApply = (it) => { setCurrent(it); setTargets({ [ilotId || ""]: true }); setView("apply"); };
  const apply = async () => {
    if (!chosenTargets.length) { toast.error("Cochez au moins une parcelle ou un îlot"); return; }
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/itineraries/${current.itinerary_id}/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ start_date: startDate, targets: chosenTargets.map((t) => ({ parcel_id: parcelId, ilot_id: t.ilot_id })) }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Ajout impossible"); return; }
      toast.success(`${d.created} brouillon${d.created > 1 ? "s" : ""} ajouté${d.created > 1 ? "s" : ""} au carnet`);
      setResult(d);
      setView("done");
      onApplied();
    } catch { toast.error("Erreur de connexion"); }
    finally { setBusy(false); }
  };

  // ---------- Éditer ----------
  const openEdit = (it) => {
    setDraft(it
      ? { itinerary_id: it.itinerary_id, name: it.name, culture: it.culture || "", is_template: !!it.is_template,
          shared: (it.shared_with || []).join(", "), steps: it.steps.map((s) => ({ ...s, kind: kindOf(s), dose: { ...(s.dose || {}) } })) }
      : { name: "", culture: parcelCulture || "", is_template: false, shared: "", steps: [] });
    setView("edit");
  };
  const fixFirst = (steps) => (steps[0] ? [{ ...steps[0], same_pass: false, delay_days: 0 }, ...steps.slice(1)] : steps)
    .map((s, i, arr) => (s.same_pass && (kindOf(s) !== "phyto" || i === 0 || kindOf(arr[i - 1]) !== "phyto") ? { ...s, same_pass: false } : s));
  const setStep = (i, patch) => setDraft((d) => ({ ...d, steps: fixFirst(d.steps.map((s, j) => (j === i ? { ...s, ...patch } : s))) }));
  const moveStep = (i, dir) => setDraft((d) => {
    const steps = [...d.steps];
    const j = i + dir;
    if (j < 0 || j >= steps.length) return d;
    [steps[i], steps[j]] = [steps[j], steps[i]];
    return { ...d, steps: fixFirst(steps) };
  });
  const removeStep = (i) => setDraft((d) => ({ ...d, steps: fixFirst(d.steps.filter((_, j) => j !== i)) }));

  // Glisser-déposer des étapes (doigt ou souris) : poignée à gauche de chaque étape
  const [drag, setDrag] = useState(null);           // { from, over }
  const stepRefs = useRef([]);
  const bodyRef = useRef(null);
  const moveStepTo = (from, to) => setDraft((d) => {
    if (from === to) return d;
    const steps = [...d.steps];
    const [moved] = steps.splice(from, 1);
    steps.splice(to, 0, moved);
    return { ...d, steps: fixFirst(steps) };
  });
  const startDrag = (e, from) => {
    e.preventDefault();
    let over = from;
    setDrag({ from, over });
    const onMove = (ev) => {
      const y = ev.clientY;
      const body = bodyRef.current?.getBoundingClientRect();
      if (body) {                                       // défilement automatique près des bords
        if (y < body.top + 48) bodyRef.current.scrollBy(0, -14);
        else if (y > body.bottom - 48) bodyRef.current.scrollBy(0, 14);
      }
      // Position finale = nombre d'autres étapes dont le milieu est au-dessus du doigt
      let to = 0;
      stepRefs.current.forEach((el, k) => {
        if (!el || k === from) return;
        const r = el.getBoundingClientRect();
        if (y > r.top + r.height / 2) to += 1;
      });
      over = to;
      setDrag({ from, over });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      moveStepTo(from, over);
      setDrag(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };
  const addProduct = (value) => {
    const item = catalogs[addKind].find((c) => c.value === value);
    if (!item) return;
    const { dose_default: dd, dar, unit, ...base } = item.step;
    const m = dd ? String(dd).match(/([\d.,]+)\s*([a-zA-Z%/]+)?/) : null;
    const darN = dar ? parseInt(String(dar).replace(/\D/g, ""), 10) : null;
    setDraft((d) => ({
      ...d,
      steps: [...d.steps, {
        ...base,
        dose: { value: m ? m[1].replace(",", ".") : "", unit: (m && m[2]) || unit || KINDS[addKind].units[0] },
        target: "", method: "", water_volume_value: null, dar_days: Number.isNaN(darN) ? null : darN,
        delay_days: d.steps.length ? 7 : 0, same_pass: false,
      }],
    }));
  };
  const save = async () => {
    if (!draft.name.trim()) { toast.error("Donnez un nom à l'itinéraire"); return; }
    if (!draft.steps.length) { toast.error("Ajoutez au moins un produit"); return; }
    if (draft.steps.some((s) => toNum(s.dose?.value) == null)) { toast.error("Indiquez la dose de chaque produit"); return; }
    setBusy(true);
    try {
      const body = {
        name: draft.name, culture: draft.culture || null, is_template: draft.is_template,
        shared_with: draft.shared.split(/[\s,;]+/).filter((e) => e.includes("@")),
        steps: draft.steps.map((s, i) => ({
          kind: kindOf(s),
          product: s.product,
          dose: { value: toNum(s.dose.value), unit: s.dose.unit || KINDS[kindOf(s)].units[0] },
          method: s.method || null,
          target: s.target || null,
          water_volume_value: kindOf(s) === "phyto" ? toNum(s.water_volume_value) : null,
          dar_days: s.dar_days ?? null,
          delay_days: i === 0 || s.same_pass ? 0 : Math.max(0, parseInt(s.delay_days, 10) || 0),
          same_pass: i > 0 && !!s.same_pass,
        })),
      };
      const url = draft.itinerary_id ? `${API_BASE}/itineraries/${draft.itinerary_id}` : `${API_BASE}/itineraries`;
      const res = await authFetch(url, { method: draft.itinerary_id ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Enregistrement impossible"); return; }
      toast.success("Itinéraire enregistré");
      load();
      setView("list");
    } catch { toast.error("Erreur de connexion"); }
    finally { setBusy(false); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Supprimer l'itinéraire « ${it.name} » ? Les brouillons déjà écrits dans les carnets ne sont pas touchés.`)) return;
    const res = await authFetch(`${API_BASE}/itineraries/${it.itinerary_id}`, { method: "DELETE" });
    if (res.ok) { toast.success("Itinéraire supprimé"); load(); } else toast.error("Suppression impossible");
  };

  // ---------- Depuis le carnet ----------
  const openFromCarnet = () => {
    setView("fromCarnet");
    if (source) return;
    const q = ilotId ? `&ilot_id=${encodeURIComponent(ilotId)}` : "";
    authFetch(`${API_BASE}/carnet/duplicate/source?parcel_id=${encodeURIComponent(parcelId)}${q}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setSource)
      .catch(() => toast.error("Chargement du carnet impossible"));
  };
  const createFromCarnet = async () => {
    const keys = (source?.items || []).filter((it) => picked[it.key]).map((it) => it.key);
    if (!fromName.trim()) { toast.error("Donnez un nom à l'itinéraire"); return; }
    if (!keys.length) { toast.error("Cochez au moins une saisie"); return; }
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/itineraries/from-carnet`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: fromName, parcel_id: parcelId, ilot_id: ilotId || null, item_keys: keys }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Création impossible"); return; }
      toast.success("Itinéraire créé à partir du carnet");
      setPicked({}); setFromName("");
      load();
      openEdit(d);
    } catch { toast.error("Erreur de connexion"); }
    finally { setBusy(false); }
  };

  const list = data?.itineraries || [];
  const sections = [
    ["Mes itinéraires", list.filter((i) => i.origin === "mine")],
    ["Partagés avec moi", list.filter((i) => i.origin === "shared")],
    ["Modèles proposés", list.filter((i) => i.origin === "template")],
  ];

  const ItinCard = ({ it }) => (
    <div className="rounded-xl border border-black/10 p-3 space-y-2" data-testid={`itinerary-${it.itinerary_id}`}>
      <div className="flex items-start gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-bold flex items-center gap-1.5 flex-wrap">
            {it.name}
            {it.culture && parcelCulture && norm(it.culture) !== norm(parcelCulture) && (
              <span className="text-[10px] font-semibold rounded-full px-1.5 py-0.5" style={{ background: "#FBF1DC", color: "#9A6A12" }}>Culture {it.culture}</span>
            )}
          </div>
          <div className="text-[11px] text-gray-500">
            {it.steps.length} étape{it.steps.length > 1 ? "s" : ""}{it.culture ? ` · ${it.culture}` : ""}
            {it.origin === "shared" && it.owner_email ? ` · de ${it.owner_name || it.owner_email}` : ""}
            {it.origin === "mine" && it.shared_with?.length ? ` · partagé avec ${it.shared_with.length} compte${it.shared_with.length > 1 ? "s" : ""}` : ""}
          </div>
        </div>
        <div className="flex gap-1.5">
          <button type="button" onClick={() => openApply(it)} className="h-8 px-3 rounded-lg text-white text-[11.5px] font-semibold inline-flex items-center gap-1" style={{ background: "#2D6A4F" }} data-testid={`itinerary-apply-${it.itinerary_id}`}>
            <Play className="w-3.5 h-3.5" /> Ajouter
          </button>
          {it.can_edit && (
            <>
              <button type="button" onClick={() => openEdit(it)} className="h-8 w-8 rounded-lg border border-black/15 grid place-items-center" aria-label="Modifier"><Pencil className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => remove(it)} className="h-8 w-8 rounded-lg border border-red-200 text-red-600 grid place-items-center" aria-label="Supprimer"><Trash2 className="w-3.5 h-3.5" /></button>
            </>
          )}
        </div>
      </div>
      <Chain steps={it.steps} />
    </div>
  );

  const title = {
    list: "Itinéraires techniques", apply: "Ajouter l'itinéraire au carnet", done: "Itinéraire ajouté",
    edit: draft?.itinerary_id ? "Modifier l'itinéraire" : "Nouvel itinéraire", fromCarnet: "Créer depuis le carnet",
  }[view];

  const renderApply = () => {
    const dates = stepDates(current.steps, startDate);
    const cultureDiff = current.culture && chosenTargets.some((t) => t.culture && norm(t.culture) !== norm(current.culture))
      && current.steps.some((s) => kindOf(s) === "phyto");
    return (
      <>
        <div className="text-[13px] font-bold">{current.name}</div>
        <div className="flex flex-wrap gap-3 items-center">
          <label className="flex items-center gap-2 text-[12.5px]">Première étape le
            <input type="date" value={startDate} onChange={(e) => e.target.value && setStartDate(e.target.value)}
              className="h-8 border border-black/15 rounded-md px-2 text-[12.5px]" data-testid="itinerary-start" />
          </label>
        </div>
        <div className="space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Appliquer sur</div>
          <div className="flex flex-wrap gap-1.5">
            {targetRows.map((t) => (
              <label key={t.key} className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] cursor-pointer ${targets[t.key] ? "border-emerald-700 bg-emerald-50/60" : "border-black/10"}`}>
                <input type="checkbox" className="accent-emerald-700" checked={!!targets[t.key]} onChange={(e) => setTargets((s) => ({ ...s, [t.key]: e.target.checked }))} />
                {t.label} <span className="text-gray-500">· {fmt(t.surface)} ha</span>
              </label>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto rounded-lg border border-black/10">
          <table className="w-full min-w-[560px] text-[11.5px]">
            <thead>
              <tr className="bg-black/[0.03] text-[10px] uppercase tracking-wide text-gray-500">
                <th className="text-left px-2 py-1.5">Date</th><th className="text-left px-2 py-1.5">Type</th>
                <th className="text-left px-2 py-1.5">Produit</th><th className="text-right px-2 py-1.5">Dose</th>
                {chosenTargets.map((t) => <th key={t.key} className="text-right px-2 py-1.5">{t.ilot_id ? t.label.replace("Îlot ", "") : "Parcelle"}<br /><span className="normal-case">{fmt(t.surface)} ha</span></th>)}
              </tr>
            </thead>
            <tbody>
              {current.steps.map((s, i) => {
                const d = toNum(s.dose?.value);
                const perHa = /\/ha$/.test(s.dose?.unit || "");
                const base = (s.dose?.unit || "").split("/")[0];
                return (
                  <tr key={i} className={`border-t border-black/5 ${s.same_pass ? "bg-black/[0.02]" : ""}`}>
                    <td className="px-2 py-1.5 whitespace-nowrap">{frDate(dates[i])}{s.same_pass && <span className="text-gray-400"> (mélange)</span>}</td>
                    <td className="px-2 py-1.5"><KindPill kind={kindOf(s)} /></td>
                    <td className="px-2 py-1.5">{s.product?.commercial_name}{s.target ? <span className="text-gray-400"> · {s.target}</span> : ""}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">{d != null ? `${fmt(d, 3)} ${s.dose.unit}` : "—"}</td>
                    {chosenTargets.map((t) => (
                      <td key={t.key} className="px-2 py-1.5 text-right whitespace-nowrap font-semibold" style={{ color: "#1f5c39" }}>
                        {d != null && perHa && t.surface ? `${fmt(d * t.surface)} ${base}` : "—"}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {cultureDiff && (
          <p className="flex gap-1.5 text-[11.5px] rounded-lg px-2.5 py-2" style={{ background: "#FBF1DC", color: "#9A6A12" }}>
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            Itinéraire prévu pour « {current.culture} » : les produits phyto sont contrôlés sur la culture de chaque cible ; un produit non autorisé bloquera la validation du brouillon.
          </p>
        )}
        <p className="text-[11.5px] text-gray-500">
          {current.steps.length * chosenTargets.length} brouillon{current.steps.length * chosenTargets.length > 1 ? "s" : ""} seront écrits
          ({current.steps.length} étape{current.steps.length > 1 ? "s" : ""} × {chosenTargets.length} cible{chosenTargets.length > 1 ? "s" : ""}).
          Les traitements phyto arrivent en brouillons du registre, les apports en « Copie à vérifier ». Une étape datée dans le futur se valide une fois sa date passée.
        </p>
      </>
    );
  };

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[2200] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="itineraries-modal">
      <div className="bg-white w-full sm:max-w-3xl max-h-[92dvh] rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden shadow-xl">
        <div className="flex items-center gap-2 px-4 py-3 text-white" style={{ background: "#2D6A4F" }}>
          <ListOrdered className="w-4 h-4" />
          <div className="text-sm font-bold flex-1">{title}</div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/15" aria-label="Fermer"><X className="w-4 h-4" /></button>
        </div>

        <div ref={bodyRef} className="p-4 space-y-3 overflow-y-auto flex-1">
          {!data ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : view === "list" ? (
            <>
              <div className="flex gap-2 flex-wrap">
                <button type="button" onClick={() => openEdit(null)} className="h-9 px-3 rounded-lg text-white text-[12px] font-semibold inline-flex items-center gap-1.5" style={{ background: "#2D6A4F" }} data-testid="itinerary-new">
                  <Plus className="w-3.5 h-3.5" /> Nouvel itinéraire
                </button>
                <button type="button" onClick={openFromCarnet} className="h-9 px-3 rounded-lg border border-black/15 text-[12px] font-semibold inline-flex items-center gap-1.5" data-testid="itinerary-from-carnet">
                  <BookmarkPlus className="w-3.5 h-3.5" /> Créer depuis ce carnet
                </button>
              </div>
              {sections.map(([label, items]) => ((items.length || label === "Mes itinéraires") ? (
                <div key={label} className="space-y-2">
                  <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</h4>
                  {items.length ? items.map((it) => <ItinCard key={it.itinerary_id} it={it} />)
                    : <p className="text-xs italic text-gray-500">Aucun itinéraire pour l'instant. Créez-en un, ou partez de saisies existantes du carnet.</p>}
                </div>
              ) : null))}
            </>
          ) : view === "apply" && current ? renderApply()
          : view === "done" && result ? (
            <div className="space-y-2" data-testid="itinerary-result">
              <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#1f5c39" }}>
                <CheckCircle2 className="w-5 h-5" /> {result.created} brouillon{result.created > 1 ? "s" : ""} ajouté{result.created > 1 ? "s" : ""}
              </div>
              {result.targets.map((t) => (
                <div key={`${t.parcel_id}|${t.ilot_id || ""}`} className="rounded-lg border border-black/10 px-3 py-2 text-[12px]">
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-gray-500">{t.created.phyto} registre phyto · {t.created.fertilisation} fertilisation · {t.created.biostimulant} biostimulant</div>
                </div>
              ))}
              <p className="text-[11.5px] text-gray-500">Vérifiez les brouillons, puis validez les entrées du registre et confirmez les apports au fil de leur réalisation.</p>
            </div>
          ) : view === "edit" && draft ? (
            <>
              <div className="flex gap-2 flex-wrap">
                <label className="flex-1 min-w-[200px] text-[11.5px] font-semibold text-gray-600">Nom
                  <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} maxLength={120}
                    placeholder="Ex. Fraise sous serre — programme automne" className="mt-1 w-full h-9 border border-black/15 rounded-md px-2 text-[12.5px]" data-testid="itinerary-name" />
                </label>
                <label className="w-44 text-[11.5px] font-semibold text-gray-600">Culture
                  <input value={draft.culture} onChange={(e) => setDraft({ ...draft, culture: e.target.value })}
                    className="mt-1 w-full h-9 border border-black/15 rounded-md px-2 text-[12.5px]" />
                </label>
              </div>
              <label className="block text-[11.5px] font-semibold text-gray-600">
                <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Partager avec des clients (e-mails de leurs comptes, séparés par des virgules)</span>
                <input value={draft.shared} onChange={(e) => setDraft({ ...draft, shared: e.target.value })}
                  placeholder="client1@exemple.re, client2@exemple.re" className="mt-1 w-full h-9 border border-black/15 rounded-md px-2 text-[12.5px] font-normal" data-testid="itinerary-share" />
                <span className="block font-normal text-[10.5px] text-gray-500 mt-0.5">Ils pourront l'appliquer à leurs parcelles et îlots, sans pouvoir le modifier.</span>
              </label>
              {data.is_admin && (
                <label className="flex items-center gap-2 text-[12px]">
                  <input type="checkbox" checked={draft.is_template} onChange={(e) => setDraft({ ...draft, is_template: e.target.checked })} className="accent-emerald-700" />
                  Publier comme modèle proposé à tous les comptes
                </label>
              )}
              <div className="space-y-1.5">
                {draft.steps.map((s, i) => {
                  const k = kindOf(s);
                  const canMix = i > 0 && k === "phyto" && kindOf(draft.steps[i - 1]) === "phyto";
                  const isDragged = drag?.from === i;
                  const dropMark = drag && drag.from !== drag.over && drag.over === i ? (drag.over < drag.from ? "before" : "after") : null;
                  return (
                    <div key={i} ref={(el) => { stepRefs.current[i] = el; }} className="space-y-1.5">
                      {i > 0 && (
                        <div className="flex items-center gap-2 pl-4 text-[11.5px] text-gray-500 flex-wrap">
                          <span className="w-0.5 h-4 bg-black/10 rounded" />
                          {s.same_pass ? <span className="font-semibold" style={{ color: "#1f5c39" }}>Même passage que l'étape précédente (mélange en cuve)</span> : (
                            <label className="flex items-center gap-1.5">
                              <input type="number" min="0" value={s.delay_days ?? 0} onChange={(e) => setStep(i, { delay_days: e.target.value })}
                                className="w-16 h-7 border border-black/15 rounded-md px-1.5 text-center" /> jours après l'étape précédente
                            </label>
                          )}
                        </div>
                      )}
                      {dropMark === "before" && <div className="h-1 rounded-full bg-emerald-600" />}
                      <div className={`rounded-xl border p-2.5 flex gap-2 items-start transition-opacity ${isDragged ? "opacity-50 border-emerald-600 ring-2 ring-emerald-600/30" : "border-black/10"}`}>
                        <button type="button" onPointerDown={(e) => startDrag(e, i)} aria-label="Glisser pour déplacer l'étape" title="Glisser pour déplacer"
                          className="h-6 w-5 -ml-1 grid place-items-center text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing shrink-0"
                          style={{ touchAction: "none" }} data-testid={`itinerary-drag-${i}`}>
                          <GripVertical className="w-4 h-4" />
                        </button>
                        <span className="w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold text-white shrink-0" style={{ background: KINDS[k].fg }}>{i + 1}</span>
                        <div className="flex-1 min-w-0 space-y-1.5">
                          <div className="flex items-center gap-1.5 min-w-0"><KindPill kind={k} /><span className="text-[12.5px] font-semibold truncate">{s.product?.commercial_name}</span></div>
                          <div className="flex flex-wrap gap-2 items-center text-[11.5px] text-gray-600">
                            <label className="flex items-center gap-1">Dose
                              <input value={s.dose?.value ?? ""} onChange={(e) => setStep(i, { dose: { ...s.dose, value: e.target.value } })} inputMode="decimal"
                                className="w-16 h-7 border border-black/15 rounded-md px-1.5 text-center" />
                              <select value={s.dose?.unit || KINDS[k].units[0]} onChange={(e) => setStep(i, { dose: { ...s.dose, unit: e.target.value } })}
                                className="h-7 border border-black/15 rounded-md px-1 bg-white">
                                {[...new Set([s.dose?.unit, ...KINDS[k].units].filter(Boolean))].map((u) => <option key={u}>{u}</option>)}
                              </select>
                            </label>
                            {k !== "fertilisation" && (
                              <label className="flex items-center gap-1">Cible
                                <input value={s.target || ""} onChange={(e) => setStep(i, { target: e.target.value })} className="w-28 h-7 border border-black/15 rounded-md px-1.5" />
                              </label>
                            )}
                            {k === "phyto" ? (
                              <label className="flex items-center gap-1">Bouillie
                                <input value={s.water_volume_value ?? ""} onChange={(e) => setStep(i, { water_volume_value: e.target.value })} inputMode="decimal"
                                  className="w-16 h-7 border border-black/15 rounded-md px-1.5 text-center" /> L/ha
                              </label>
                            ) : (
                              <label className="flex items-center gap-1">Méthode
                                <input value={s.method || ""} onChange={(e) => setStep(i, { method: e.target.value })} className="w-28 h-7 border border-black/15 rounded-md px-1.5" />
                              </label>
                            )}
                            {canMix && (
                              <label className="flex items-center gap-1">
                                <input type="checkbox" checked={!!s.same_pass} onChange={(e) => setStep(i, { same_pass: e.target.checked, delay_days: 0 })} className="accent-emerald-700" />
                                Même passage que la précédente
                              </label>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <button type="button" onClick={() => moveStep(i, -1)} disabled={i === 0} className="h-6 w-6 rounded border border-black/15 grid place-items-center disabled:opacity-30" aria-label="Monter"><ArrowUp className="w-3 h-3" /></button>
                          <button type="button" onClick={() => moveStep(i, 1)} disabled={i === draft.steps.length - 1} className="h-6 w-6 rounded border border-black/15 grid place-items-center disabled:opacity-30" aria-label="Descendre"><ArrowDown className="w-3 h-3" /></button>
                          <button type="button" onClick={() => removeStep(i)} className="h-6 w-6 rounded border border-red-200 text-red-600 grid place-items-center" aria-label="Retirer"><X className="w-3 h-3" /></button>
                        </div>
                      </div>
                      {dropMark === "after" && <div className="h-1 rounded-full bg-emerald-600" />}
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 items-center flex-wrap">
                <div className="inline-flex rounded-lg border border-black/15 overflow-hidden">
                  {Object.entries(KINDS).map(([k, v]) => (
                    <button key={k} type="button" onClick={() => setAddKind(k)} className="px-2.5 h-9 text-[11.5px] font-semibold"
                      style={addKind === k ? { background: v.fg, color: "#fff" } : { background: "#fff", color: "#4A4A4A" }}>{v.label}</button>
                  ))}
                </div>
                <div className="flex-1 min-w-[220px]">
                  <SearchableSelect key={addKind} items={catalogs[addKind]} value="" onSelect={addProduct}
                    placeholder={`+ Ajouter un produit ${KINDS[addKind].label.toLowerCase()}`} testId="itinerary-add-product" />
                </div>
              </div>
              {draft.steps.length > 0 && (
                <p className="text-[11.5px] rounded-lg px-2.5 py-2" style={{ background: "#E3EFE8", color: "#1F5A40" }}>
                  Calendrier : {(() => { let day = 0; return draft.steps.map((s, i) => { if (i > 0 && !s.same_pass) day += Number(s.delay_days) || 0; return `J+${day} ${s.product?.commercial_name}`; }).join(" · "); })()}
                </p>
              )}
            </>
          ) : view === "fromCarnet" ? (
            <>
              <p className="text-[12px] text-gray-600">Cochez les saisies de ce carnet à reprendre (registre, fertilisation, biostimulants). L'ordre, les délais entre dates et les mélanges sont repris ; vous pourrez tout ajuster ensuite.</p>
              <input value={fromName} onChange={(e) => setFromName(e.target.value)} maxLength={120} placeholder="Nom de l'itinéraire"
                className="w-full h-9 border border-black/15 rounded-md px-2 text-[12.5px]" data-testid="itinerary-from-name" />
              {!source ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                : source.items.length === 0 ? <p className="text-xs italic text-gray-500">Aucune saisie dans ce carnet.</p> : (
                  <div className="space-y-1.5">
                    <button type="button" className="text-[11.5px] underline text-gray-600"
                      onClick={() => setPicked(Object.fromEntries(source.items.map((it) => [it.key, true])))}>Tout cocher</button>
                    {source.items.map((it) => (
                      <label key={it.key} className={`flex gap-2.5 items-start rounded-lg border px-3 py-2 cursor-pointer ${picked[it.key] ? "border-emerald-700 bg-emerald-50/50" : "border-black/10"}`}>
                        <input type="checkbox" className="mt-1 accent-emerald-700" checked={!!picked[it.key]} onChange={(e) => setPicked((s) => ({ ...s, [it.key]: e.target.checked }))} />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5 text-[12px] font-semibold">{frDate(it.date)} <KindPill kind={it.type} />{it.products.length > 1 ? <span className="text-gray-400 font-normal">mélange</span> : null}</span>
                          <span className="block text-[11.5px] text-gray-600">
                            {it.products.map((p) => `${p.name}${p.dose != null && p.dose !== "" ? ` ${fmt(toNum(p.dose), 3)} ${p.unit || ""}` : ""}`).join(" + ")}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
            </>
          ) : null}
        </div>

        {data && view !== "list" && (
          <div className="flex justify-between gap-2 px-4 py-3 border-t border-black/5">
            <button type="button" onClick={() => (view === "done" ? onClose() : setView("list"))} className="h-9 px-4 rounded-lg border border-black/15 text-xs font-semibold">
              {view === "done" ? "Fermer" : "Retour"}
            </button>
            {view === "apply" && (
              <button type="button" disabled={busy || !chosenTargets.length} onClick={apply} data-testid="itinerary-apply-confirm"
                className="h-9 px-4 rounded-lg text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" style={{ background: "#2D6A4F" }}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Écrire les brouillons
              </button>
            )}
            {view === "edit" && (
              <button type="button" disabled={busy} onClick={save} data-testid="itinerary-save"
                className="h-9 px-4 rounded-lg text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" style={{ background: "#2D6A4F" }}>
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Enregistrer l'itinéraire
              </button>
            )}
            {view === "fromCarnet" && (
              <button type="button" disabled={busy} onClick={createFromCarnet} data-testid="itinerary-from-save"
                className="h-9 px-4 rounded-lg text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" style={{ background: "#2D6A4F" }}>
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Enregistrer comme itinéraire
              </button>
            )}
          </div>
        )}
      </div>
    </div>
    </ModalPortal>
  );
}
